import { CartAddEvent, CartErrorEvent } from '@theme/events';

// No prices, currency, inventory or variant names are stored in this script.
export function groupItems(ids) {
  const counts = new Map();
  for (const id of ids) counts.set(String(id), (counts.get(String(id)) || 0) + 1);
  return [...counts].map(([id, quantity]) => ({id: Number(id), quantity}));
}
export function packPrice(ids, variants, discount) {
  let gross = 0, total = 0, before = 0;
  for (const item of groupItems(ids)) {
    const variant = variants.find(v => String(v.id) === String(item.id));
    if (!variant) return null;
    const line = variant.price * item.quantity;
    gross += line;
    before += discount === 0 ? Math.max(variant.price, variant.compareAt || 0) * item.quantity : line;
    total += line - Math.round(line * discount / 100);
  }
  return {gross, before, total, each: Math.round(total / ids.length)};
}
export function validItems(items, variants, cartItems = []) {
  return items.length > 0 && items.every(item => {
    const v = variants.find(v => String(v.id) === String(item.id));
    if (!v?.available) return false;
    const inCart = cartItems.filter(i => String(i.variant_id || i.id) === String(item.id)).reduce((sum, i) => sum + i.quantity, 0);
    const combined = item.quantity + inCart;
    if (v.inventoryManagement && v.inventoryPolicy !== 'continue' && combined > v.inventoryQuantity) return false;
    if (v.maximum && combined > v.maximum) return false;
    if (combined < (v.minimum || 1)) return false;
    return combined % (v.increment || 1) === 0;
  });
}

class BeamPheromonesProduct extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.controller = new AbortController();
    this.data = JSON.parse(this.querySelector('[data-bp-product]').textContent);
    this.cards = [...this.querySelectorAll('[data-bp-pack]')];
    this.form = this.querySelector('form');
    this.fields = this.querySelector('[data-scent-fields]');
    this.button = this.querySelector('[data-bp-add]');
    this.dialog = this.querySelector('.bp-scent-dialog');
    this.guideTarget = this.querySelector('[data-guide-target]');
    this.quantity = Number(this.dataset.defaultQuantity);
    if (!this.cards.some(c => Number(c.dataset.bpPack) === this.quantity)) this.quantity = Number(this.cards[0]?.dataset.bpPack) || 1;
    const available = this.data.variants.filter(v => v.available);
    const selected = this.data.variants.find(v => String(v.id) === String(this.data.selected) && v.available) || available[0] || this.data.variants[0];
    const rest = available.filter(v => v.id !== selected?.id);
    this.choices = [selected?.id, ...rest.map(v => v.id)];
    this.fillChoices();
    const signal = this.controller.signal;
    this.addEventListener('click', event => {
      const card = event.target.closest('[data-bp-pack]');
      if (card && !card.disabled) { this.choosePack(Number(card.dataset.bpPack)); return; }
      const open = event.target.closest('[data-open-scent-guide]');
      if (open) this.openGuide(open);
      if (event.target.closest('[data-close-scent-guide]')) this.dialog.close();
      const choice = event.target.closest('[data-bp-scent]');
      if (choice && this.dialog.contains(choice) && !choice.disabled) {
        const index = Math.max(0, Math.min(this.quantity - 1, Number(this.guideTarget.value) || 0));
        this.chooseScent(index, choice.dataset.bpScent);
        this.returnFocus = this.fields.querySelector(`[data-bp-scent-index="${index}"]`);
        this.dialog.close();
      }
    }, {signal});
    this.fields.addEventListener('change', event => {
      const input = event.target.closest('[data-bp-scent-index]');
      if (input) this.chooseScent(Number(input.dataset.bpScentIndex), input.value);
    }, {signal});
    this.dialog.addEventListener('click', event => {
      if (event.target !== this.dialog) return;
      const rect = this.dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
    }, {signal});
    this.dialog.addEventListener('close', () => {
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.returnFocus?.focus({preventScroll: true});
    }, {signal});
    this.form.addEventListener('submit', event => {
      event.preventDefault();
      // Exactly one multi-item request; avoid third-party delegated form handlers
      // replacing the selection with a single variant or an old app deal.
      event.stopImmediatePropagation();
      this.submit();
    }, {signal, capture: true});
    document.addEventListener('beam:choose-pack', event => this.choosePack(Number(event.detail.quantity), true), {signal});
    document.addEventListener('beam:choose-scent', event => {
      this.chooseScent(0, event.detail.id);
      this.fields.scrollIntoView({behavior: this.motion(), block: 'center'});
    }, {signal});
    // Deliberately no gallery selection here. The server renders product.featured_media
    // first even with a ?variant= URL or a preselected pack/scent.
    this.render();
  }
  disconnectedCallback() {
    this.controller?.abort();
    if (this.dialog?.open) this.dialog.close();
    this.initialized = false;
  }
  motion() { return matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'; }
  fillChoices() {
    const fallback = this.choices[0];
    const maximum = Math.max(this.quantity, ...this.cards.map(c => Number(c.dataset.bpPack)));
    while (this.choices.length < maximum) this.choices.push(fallback);
  }
  money(cents) {
    const currency = this.data.currency;
    const display = this.data.currencyCode ? 'code' : 'symbol';
    // Shopify Ajax/Liquid prices are expressed in hundredths also for JPY/KRW.
    return new Intl.NumberFormat(this.data.locale || document.documentElement.lang, {style:'currency',currency,currencyDisplay:display}).format(cents / 100);
  }
  choosePack(quantity, scroll = false) {
    if (this.busy) return;
    const card = this.cards.find(c => Number(c.dataset.bpPack) === quantity);
    if (!card || card.disabled) return;
    this.quantity = quantity;
    this.fillChoices();
    this.render();
    if (scroll) this.querySelector('.bp-bundles').scrollIntoView({behavior:this.motion(),block:'center'});
  }
  chooseScent(index, id) {
    if (this.busy) return;
    const variant = this.data.variants.find(v => String(v.id) === String(id));
    if (!variant?.available) return;
    this.choices[index] = variant.id;
    this.render(false);
    if (index === 0) {
      const url = new URL(location.href);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
    // This is the only path that changes the photo: a deliberate scent choice.
    if (variant.media) {
      const gallery = this.querySelector('media-gallery');
      const slides = [...(gallery?.querySelectorAll('slideshow-slide[data-bp-media-id]') || [])];
      const slideIndex = slides.findIndex(s => String(s.dataset.bpMediaId) === String(variant.media));
      if (slideIndex >= 0) gallery.slideshow?.select(slideIndex, undefined, {animate:false});
    }
    this.querySelector('[data-bp-live]').textContent = `${this.data.labels.bottle} ${index+1}: ${variant.title}`;
  }
  render(rebuildFields = true) {
    this.fillChoices();
    const selectedCard = this.cards.find(c => Number(c.dataset.bpPack) === this.quantity);
    const price = packPrice(this.choices.slice(0, this.quantity), this.data.variants, Number(selectedCard?.dataset.discount || 0));
    for (const card of this.cards) {
      const qty = Number(card.dataset.bpPack);
      const cardPrice = packPrice(this.choices.slice(0, qty), this.data.variants, Number(card.dataset.discount));
      card.setAttribute('aria-pressed', String(qty === this.quantity));
      card.disabled = this.busy || !this.data.variants.some(v => v.available);
      if (cardPrice) {
        const before = card.querySelector('[data-bp-before]');
        before.textContent = this.money(cardPrice.before);
        before.hidden = cardPrice.before <= cardPrice.total;
        card.querySelector('[data-bp-amount]').textContent = this.money(cardPrice.total);
        const each = card.querySelector('[data-bp-each]');
        if (each) each.textContent = this.money(cardPrice.each);
        document.querySelectorAll(`[data-bp-recap-price="${qty}"]`).forEach(el=>el.textContent=this.money(cardPrice.total));
      }
    }
    if (rebuildFields || this.fields.children.length !== this.quantity) {
      this.fields.replaceChildren();
      for (let index=0; index<this.quantity; index++) {
        const label=document.createElement('label');label.textContent=`${this.data.labels.bottle} ${index+1}`;
        const select=document.createElement('select');select.dataset.bpScentIndex=index;select.disabled=!!this.busy;
        select.setAttribute('aria-label',`${this.data.labels.scent} ${index+1}`);
        if(index===0) select.name='id';
        for (const variant of this.data.variants) {
          const option=document.createElement('option');option.value=variant.id;
          option.textContent=variant.title+(variant.available?'':` · ${this.data.labels.soldOut}`);
          option.disabled=!variant.available;select.append(option);
        }
        select.value=String(this.choices[index]);label.append(select);this.fields.append(label);
      }
    } else this.fields.querySelectorAll('select').forEach((el,i)=>{el.value=String(this.choices[i]);el.disabled=!!this.busy;});
    const valid = validItems(groupItems(this.choices.slice(0,this.quantity)),this.data.variants);
    this.button.disabled = this.busy || !valid;
    this.button.setAttribute('aria-busy',String(!!this.busy));
    this.querySelector('[data-bp-add-label]').textContent=this.busy?this.data.labels.adding:(this.data.variants.some(v=>v.available)?this.data.labels.add:this.data.labels.soldOut);
    this.querySelector('[data-bp-total]').textContent=price?this.money(price.total):'';
    const error=this.querySelector('[data-bp-error]');
    error.hidden=valid;
    if(!valid)error.textContent=this.data.labels.quantityError;
    const accelerated=this.querySelector('[data-bp-accelerated]');
    if(accelerated)accelerated.hidden=this.quantity!==1 || !valid || !!this.busy;
    this.querySelectorAll('[data-open-scent-guide]').forEach(b=>b.disabled=!!this.busy);
  }
  openGuide(trigger) {
    if (this.busy || this.dialog.open) return;
    this.returnFocus=trigger;
    this.guideTarget.replaceChildren();
    for(let i=0;i<this.quantity;i++){const o=document.createElement('option');o.value=i;o.textContent=`${this.data.labels.bottle} ${i+1}`;this.guideTarget.append(o);}
    this.previousOverflow=document.documentElement.style.overflow;
    document.documentElement.style.overflow='hidden';
    this.dialog.showModal();
  }
  async cart() {
    const response=await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`,{headers:{Accept:'application/json'},cache:'no-store'});
    if(!response.ok)throw new Error(this.data.labels.networkError);
    return response.json();
  }
  async submit() {
    if(this.busy)return;
    const items=groupItems(this.choices.slice(0,this.quantity));
    if(!validItems(items,this.data.variants)){this.render();return;}
    this.busy=true;this.render(false);
    let added=false, errorMessage='';
    try{
      const before=await this.cart();
      if(!validItems(items,this.data.variants,before.items))throw new Error(this.data.labels.quantityError);
      const sectionIds=[...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(e=>e.dataset.sectionId))].slice(0,5);
      const route=`${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const formData=new FormData(this.form);
      const properties={};for(const [key,value] of formData){const m=key.match(/^properties\[(.+)\]$/);if(m && typeof value==='string')properties[m[1]]=value;}
      if(Object.keys(properties).length)items.forEach(i=>i.properties={...properties});
      const response=await fetch(route,{method:'POST',headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections:sectionIds,sections_url:location.pathname+location.search})});
      const result=await response.json();
      if(!response.ok || result.status){
        const actual=await this.cart().catch(()=>null);
        this.dispatchEvent(new CartAddEvent(actual || {},this.id || this.dataset.sectionId,{source:'product-form-component',productId:this.data.id,didError:true}));
        throw new Error(result.description || result.message || this.data.labels.quantityError);
      }
      added=true;
      const actual=await this.cart().catch(()=>null);
      this.dispatchEvent(new CartAddEvent(actual || undefined,String(this.choices[0]),{source:'product-form-component',productId:this.data.id,itemCount:this.quantity,sections:result.sections}));
      this.querySelector('[data-bp-live]').textContent=this.data.labels.added;
      // Upcart is already installed in the supplied theme; otherwise use Tinker.
      if(typeof window.upcartOpenCart==='function') {
        if(typeof window.upcartRefreshCart==='function') await window.upcartRefreshCart();
        window.upcartOpenCart();
      } else {
        const drawer=document.querySelector('cart-drawer-component');
        if(drawer?.open)drawer.open();else location.assign(this.dataset.cartUrl);
      }
    }catch(error){
      if(added){location.assign(this.dataset.cartUrl);return;}
      errorMessage=error?.message || this.data.labels.networkError;
      this.dispatchEvent(new CartErrorEvent(this.dataset.sectionId,errorMessage,errorMessage,{}));
    }finally{
      this.busy=false;this.render(false);
      if(errorMessage){const error=this.querySelector('[data-bp-error]');error.textContent=errorMessage;error.hidden=false;}
    }
  }
}
if(!customElements.get('beam-pheromones-product'))customElements.define('beam-pheromones-product',BeamPheromonesProduct);

// Delegation also works after section reloads in the Shopify theme editor.
if(!window.beamPheromonesSectionsReady){
  window.beamPheromonesSectionsReady=true;
  document.addEventListener('click',event=>{
    const recap=event.target.closest('.bp-root [data-bp-recap-pack]');
    if(recap)document.dispatchEvent(new CustomEvent('beam:choose-pack',{detail:{quantity:recap.dataset.bpRecapPack}}));
    const scent=event.target.closest('.bp-root [data-bp-scent]');
    if(scent && !scent.closest('dialog') && !scent.disabled)document.dispatchEvent(new CustomEvent('beam:choose-scent',{detail:{id:scent.dataset.bpScent}}));
    const more=event.target.closest('.bp-root [data-more-reviews]');
    if(more){const section=more.closest('[data-bp-reviews]');section.querySelectorAll('[data-bp-review]').forEach(r=>r.hidden=false);more.hidden=true;}
  });
  const initReviews=(scope)=>scope.querySelectorAll('[data-bp-reviews]').forEach(section=>{
    if(section.dataset.ready)return;section.dataset.ready='true';
    const count=Number(section.dataset.bpReviews),reviews=[...section.querySelectorAll('[data-bp-review]')];
    reviews.forEach((r,i)=>r.hidden=i>=count);
    const button=section.querySelector('[data-more-reviews]');if(button)button.hidden=reviews.length<=count;
  });
  initReviews(document);
  document.addEventListener('shopify:section:load',event=>{initReviews(event.target);document.querySelector('beam-pheromones-product')?.render(false);});
  document.addEventListener('shopify:block:select',event=>{
    const review=event.target.closest('[data-bp-review]');if(review)review.hidden=false;
  });
}
